import React, { useState, useEffect } from 'react';
import { useRunPlan } from '../context/RunPlanContext';
import { useSettings } from '../context/SettingsContext';
import useMediaQuery from '../hooks/useMediaQuery';
import useLocalStorage from '../hooks/useLocalStorage';
import { generateRandomAcorn, calculateAcornDrops, mysterySeeds, acornTypes, getGrowthStage } from '../data/acornData';
import { Sprout, Sparkles, Info, Book } from 'lucide-react';
import toast from 'react-hot-toast';

const ScoutView = () => {
  const { runs } = useRunPlan();
  const { darkMode, t } = useSettings();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Scout garden state
  const [acornStash, setAcornStash] = useLocalStorage('scoutAcornStash', []);
  const [growingGarden, setGrowingGarden] = useLocalStorage('scoutGrowingGarden', []);
  const [forestCollection, setForestCollection] = useLocalStorage('scoutForestCollection', []);
  const [lastProcessedMiles, setLastProcessedMiles] = useLocalStorage('scoutLastProcessedMiles', 0);
  const [expansion1Unlocked, setExpansion1Unlocked] = useLocalStorage('scoutExpansion1', false);
  const [expansion2Unlocked, setExpansion2Unlocked] = useLocalStorage('scoutExpansion2', false);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useLocalStorage('scoutHasSeenOnboarding', false);

  const [selectedAcornToPlant, setSelectedAcornToPlant] = useState(null);
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [harvestingSeed, setHarvestingSeed] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showCollectionGuide, setShowCollectionGuide] = useState(false);

  // Calculate max slots based on expansions
  const baseSlots = 9;
  const maxSlots = baseSlots + (expansion1Unlocked ? 3 : 0) + (expansion2Unlocked ? 3 : 0);

  // Migrate old seeds to mystery format (run once)
  useEffect(() => {
    // Convert stash seeds to mystery format
    const updatedStash = acornStash.map(seed => {
      // If seed doesn't have the mystery format (old seed), convert it
      const mysterySeed = mysterySeeds[seed.rarity];
      if (mysterySeed && seed.name !== mysterySeed.name) {
        return {
          ...seed,
          name: mysterySeed.name,
          emoji: mysterySeed.emoji
        };
      }
      return seed;
    });

    // Convert growing seeds to mystery format
    const updatedGarden = growingGarden.map(seed => {
      const mysterySeed = mysterySeeds[seed.rarity];
      if (mysterySeed && seed.name !== mysterySeed.name) {
        return {
          ...seed,
          name: mysterySeed.name,
          emoji: mysterySeed.emoji
        };
      }
      return seed;
    });

    // Only update if something changed
    if (JSON.stringify(updatedStash) !== JSON.stringify(acornStash)) {
      setAcornStash(updatedStash);
    }
    if (JSON.stringify(updatedGarden) !== JSON.stringify(growingGarden)) {
      setGrowingGarden(updatedGarden);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount to migrate old data format

  // Calculate total completed miles
  const totalMiles = React.useMemo(() => {
    if (!runs) return 0;
    return Object.values(runs).reduce((total, run) => {
      if (run && (run.status === 'completed' || run.status === 'adjusted')) {
        const distance = run.status === 'adjusted' ? run.actualDistance : run.distance;
        return total + (parseFloat(distance) || 0);
      }
      return total;
    }, 0);
  }, [runs]);

  // Show onboarding modal for first-time users
  useEffect(() => {
    if (!hasSeenOnboarding && acornStash.length === 0 && growingGarden.length === 0 && forestCollection.length === 0) {
      // Show onboarding after a short delay
      const timer = setTimeout(() => {
        setShowInfoModal(true);
        setHasSeenOnboarding(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hasSeenOnboarding, acornStash.length, growingGarden.length, forestCollection.length, setHasSeenOnboarding]);

  // Auto-unlock expansions based on total miles with celebration
  useEffect(() => {
    if (totalMiles >= 50 && !expansion1Unlocked) {
      setExpansion1Unlocked(true);
      toast.success('🎉 Garden Expansion 1 unlocked! +3 slots', {
        duration: 4000,
        icon: '✨'
      });
    }
    if (totalMiles >= 100 && !expansion2Unlocked && expansion1Unlocked) {
      setExpansion2Unlocked(true);
      toast.success('🎉 Garden Expansion 2 unlocked! +3 slots', {
        duration: 4000,
        icon: '✨'
      });
    }
  }, [totalMiles, expansion1Unlocked, expansion2Unlocked, setExpansion1Unlocked, setExpansion2Unlocked]);

  // Process new miles and drop acorns
  useEffect(() => {
    const newMiles = totalMiles - lastProcessedMiles;

    if (newMiles > 0) {
      // Calculate total occupied slots (stash + growing garden)
      const totalOccupiedSlots = acornStash.length + growingGarden.length;
      const availableSlots = maxSlots - totalOccupiedSlots;

      // Drop acorns based on new miles, but only if there are available slots
      const acornsToAdd = calculateAcornDrops(newMiles);
      const acornsWeCanAdd = Math.min(acornsToAdd, availableSlots);
      const newAcorns = [];

      for (let i = 0; i < acornsWeCanAdd; i++) {
        newAcorns.push(generateRandomAcorn());
      }

      if (newAcorns.length > 0) {
        setAcornStash(prev => [...prev, ...newAcorns]);

        // Show toast notification for seed drops
        const rarityCount = {};
        newAcorns.forEach(acorn => {
          rarityCount[acorn.rarity] = (rarityCount[acorn.rarity] || 0) + 1;
        });

        // Create message showing rarity breakdown
        const rarityMessages = Object.entries(rarityCount).map(([rarity, count]) => {
          const seed = mysterySeeds[rarity];
          return `${seed.emoji} ${count} ${rarity}`;
        }).join(', ');

        toast.success(`Found ${newAcorns.length} seed${newAcorns.length > 1 ? 's' : ''}! ${rarityMessages}`, {
          duration: 4000,
          icon: '🌰',
          style: {
            background: darkMode ? '#1e293b' : '#ffffff',
            color: darkMode ? '#ffffff' : '#0f172a',
          }
        });
      }

      // Notify if garden is full and seeds were lost
      if (acornsToAdd > acornsWeCanAdd && acornsToAdd > 0) {
        const missedSeeds = acornsToAdd - acornsWeCanAdd;
        toast.error(`Garden full! ${missedSeeds} seed${missedSeeds > 1 ? 's' : ''} lost. Plant some seeds or unlock expansions!`, {
          duration: 4000,
          icon: '🌰'
        });
      }

      // Distribute new miles to growing acorns using strict queue system
      // Miles only apply to the first unsprouted seed, with spillover if completed
      let milesToDistribute = newMiles;
      const updatedGarden = [...growingGarden];
      const newlySproutedSeeds = []; // Track seeds that just became ready

      for (let i = 0; i < updatedGarden.length && milesToDistribute > 0; i++) {
        const acorn = updatedGarden[i];

        // Skip seeds that are already ready to sprout
        if (acorn.readyToSprout) continue;

        const milesNeeded = acorn.milesNeeded - acorn.currentMiles;
        const milesToAdd = Math.min(milesNeeded, milesToDistribute);

        acorn.currentMiles += milesToAdd;
        milesToDistribute -= milesToAdd;

        // Mark as ready to sprout when fully grown
        if (acorn.currentMiles >= acorn.milesNeeded) {
          acorn.readyToSprout = true;
          newlySproutedSeeds.push(acorn);
          // Remaining miles will cascade to next seed in queue
        } else {
          // If this seed isn't complete, stop here (strict queue - no splitting miles)
          break;
        }
      }

      // Show toast notification for newly sprouted seeds
      if (newlySproutedSeeds.length > 0) {
        newlySproutedSeeds.forEach(seed => {
          toast.success(`🌱 ${seed.name} is ready to harvest!`, {
            duration: 5000,
            icon: '✨',
            style: {
              background: darkMode ? '#1e293b' : '#ffffff',
              color: darkMode ? '#ffffff' : '#0f172a',
            }
          });
        });
      }

      setGrowingGarden(updatedGarden);
      setLastProcessedMiles(totalMiles);
    }
  }, [totalMiles, lastProcessedMiles, growingGarden, acornStash.length, maxSlots, darkMode, setAcornStash, setGrowingGarden, setForestCollection, setLastProcessedMiles]);

  // Plant an acorn from stash
  const plantAcorn = (acorn) => {
    const acornIndex = acornStash.findIndex(a => a.id === acorn.id);
    if (acornIndex === -1) return;

    const newAcornStash = [...acornStash];
    newAcornStash.splice(acornIndex, 1);
    setAcornStash(newAcornStash);

    setGrowingGarden(prev => [...prev, {
      ...acorn,
      currentMiles: 0,
      plantedOn: new Date().toISOString()
    }]);

    setShowPlantModal(false);
    setSelectedAcornToPlant(null);

    // Track seed planting in Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'plant_seed', {
        seed_name: acorn.name,
        seed_rarity: acorn.rarity,
        miles_needed: acorn.milesNeeded
      });
    }
  };

  // Show discard confirmation
  const showDiscardConfirmation = () => {
    setShowPlantModal(false);
    setShowDiscardConfirm(true);
  };

  // Discard an acorn from stash (after confirmation)
  const discardAcorn = () => {
    if (!selectedAcornToPlant) return;

    const acornIndex = acornStash.findIndex(a => a.id === selectedAcornToPlant.id);
    if (acornIndex === -1) return;

    const newAcornStash = [...acornStash];
    newAcornStash.splice(acornIndex, 1);
    setAcornStash(newAcornStash);

    // Close the modals
    setShowDiscardConfirm(false);
    setSelectedAcornToPlant(null);

    toast.success('Seed discarded', { icon: '🗑️' });
  };

  // Harvest a fully grown seed - show celebration then move to collection
  const harvestSeed = (seed) => {
    setHarvestingSeed(seed);
    setShowCelebration(true);
  };

  // Complete the harvest after celebration
  const completeCelebration = () => {
    if (!harvestingSeed) return;

    // Remove from garden
    const updatedGarden = growingGarden.filter(s => s.id !== harvestingSeed.id);
    setGrowingGarden(updatedGarden);

    // Add to collection
    const newTree = {
      ...harvestingSeed.result,
      grownOn: new Date().toISOString(),
      id: `tree_${Date.now()}`
    };
    setForestCollection(prev => [...prev, newTree]);

    // Track tree harvest in Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'harvest_tree', {
        tree_name: harvestingSeed.result.name,
        seed_rarity: harvestingSeed.rarity,
        miles_required: harvestingSeed.milesNeeded
      });
    }

    // Close modal
    setShowCelebration(false);
    setHarvestingSeed(null);
  };

  // Create slot array combining growing and stash seeds with empty slots
  const allSlots = React.useMemo(() => {
    const slots = [];

    // Add growing seeds
    growingGarden.forEach(seed => {
      slots.push({ type: 'growing', data: seed });
    });

    // Add stash seeds
    acornStash.forEach(seed => {
      slots.push({ type: 'stash', data: seed });
    });

    // Fill remaining with empty slots
    const emptyCount = maxSlots - slots.length;
    for (let i = 0; i < emptyCount; i++) {
      slots.push({ type: 'empty' });
    }

    return slots;
  }, [growingGarden, acornStash, maxSlots]);

  // Group forest collection by tree name and count duplicates
  const groupedCollection = React.useMemo(() => {
    return forestCollection.reduce((acc, tree) => {
      const treeName = tree.name;
      if (!acc[treeName]) {
        acc[treeName] = {
          ...tree,
          count: 0,
          firstGrown: tree.grownOn
        };
      }
      acc[treeName].count++;
      // Keep track of earliest grown date
      if (tree.grownOn < acc[treeName].firstGrown) {
        acc[treeName].firstGrown = tree.grownOn;
      }
      return acc;
    }, {});
  }, [forestCollection]);

  // Calculate discovery progress
  const totalPossibleTrees = acornTypes.length;
  const uniqueDiscoveries = Object.keys(groupedCollection).length;

  return (
    <div className={darkMode ? 'min-h-screen pb-6' : 'min-h-screen pb-6'}>
      {/* Header */}
      <div className={isDesktop ? 'max-w-4xl mx-auto px-6 py-6' : 'px-6 py-6'}>
        <div className="text-center mb-6 relative">
          <button
            onClick={() => setShowInfoModal(true)}
            className={`absolute right-0 top-0 p-2 rounded-xl ${darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'} transition-colors`}
          >
            <Info className={darkMode ? 'w-6 h-6 text-slate-400' : 'w-6 h-6 text-slate-500'} />
          </button>
          <div className="text-6xl mb-3">🐿️</div>
          <h2 className="text-2xl font-bold mb-2">Scout's Forest</h2>
          <p className={darkMode ? 'text-slate-400 font-quicksand' : 'text-slate-600 font-quicksand'}>
            Plant seeds and grow them into trees with every mile you run!
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className={darkMode
            ? 'bg-slate-800 rounded-xl p-4 border border-slate-700 text-center'
            : 'bg-white rounded-xl p-4 shadow-md border border-slate-200 text-center'
          }>
            <div className="text-2xl mb-1">🌰</div>
            <div className="text-2xl font-bold">{acornStash.length}</div>
            <div className={darkMode ? 'text-slate-400 text-xs' : 'text-slate-600 text-xs'}>
              In Stash
            </div>
          </div>

          <div className={darkMode
            ? 'bg-slate-800 rounded-xl p-4 border border-slate-700 text-center'
            : 'bg-white rounded-xl p-4 shadow-md border border-slate-200 text-center'
          }>
            <div className="text-2xl mb-1">🌱</div>
            <div className="text-2xl font-bold">{growingGarden.length}</div>
            <div className={darkMode ? 'text-slate-400 text-xs' : 'text-slate-600 text-xs'}>
              Growing
            </div>
          </div>

          <div className={darkMode
            ? 'bg-slate-800 rounded-xl p-4 border border-slate-700 text-center'
            : 'bg-white rounded-xl p-4 shadow-md border border-slate-200 text-center'
          }>
            <div className="text-2xl mb-1">🌳</div>
            <div className="text-2xl font-bold">{forestCollection.length}</div>
            <div className={darkMode ? 'text-slate-400 text-xs' : 'text-slate-600 text-xs'}>
              Collected
            </div>
          </div>
        </div>

        {/* Seeds & Garden - Combined View */}
        <div className={darkMode
          ? 'bg-slate-800 rounded-2xl p-6 mb-6 border border-slate-700'
          : 'bg-white rounded-2xl p-6 mb-6 shadow-md border border-slate-200'
        }>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sprout className={'w-5 h-5 ' + t.text} />
              <h3 className="text-lg font-bold">Planting Area</h3>
            </div>
            <div className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>
              {acornStash.length + growingGarden.length}/{maxSlots} slots
            </div>
          </div>

          {allSlots.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">🏃💨</div>
              <p className={darkMode ? 'text-slate-300 font-semibold mb-2' : 'text-slate-700 font-semibold mb-2'}>
                Your garden is empty!
              </p>
              <p className={darkMode ? 'text-slate-400 text-sm font-quicksand' : 'text-slate-600 text-sm font-quicksand'}>
                Complete runs to collect mystery seeds and start growing your forest. You'll earn ~1 seed per 3 miles!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {allSlots.map((slot, idx) => {
                if (slot.type === 'empty') {
                  return (
                    <div
                      key={`empty-${idx}`}
                      className={`p-4 rounded-xl border-2 border-dashed ${darkMode ? 'border-slate-700 bg-slate-900' : 'border-slate-300 bg-slate-50'} flex items-center justify-center min-h-[100px]`}
                    >
                      <div className="text-3xl opacity-20">🌰</div>
                    </div>
                  );
                }

                if (slot.type === 'growing') {
                  const acorn = slot.data;
                  const progress = (acorn.currentMiles / acorn.milesNeeded) * 100;
                  const isReady = acorn.readyToSprout;
                  const isFirstGrowing = idx === growingGarden.findIndex((_, i) => i === 0 || allSlots.slice(0, idx).filter(s => s.type === 'growing')[0]?.data.id === acorn.id);
                  const isCurrentlyGrowing = !isReady && growingGarden.findIndex(g => !g.readyToSprout) === growingGarden.findIndex(g => g.id === acorn.id);
                  const CardWrapper = isReady ? 'button' : 'div';
                  const cardWrapperProps = isReady ? { type: 'button' } : {};

                  return (
                    <CardWrapper
                      key={acorn.id}
                      onClick={isReady ? () => harvestSeed(acorn) : undefined}
                      className={`p-4 rounded-xl border-2 relative flex flex-col items-center text-center gap-3 min-h-[130px] ${
                        isReady
                          ? `${darkMode ? 'bg-gradient-to-br from-emerald-900 to-emerald-800 border-emerald-500' : 'bg-gradient-to-br from-emerald-100 to-emerald-200 border-emerald-400'} animate-pulse cursor-pointer hover:scale-105 transition-transform`
                          : `${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-100 border-slate-300'}`
                      }`}
                      {...cardWrapperProps}
                    >
                      {/* Queue indicator for first growing seed */}
                      {isCurrentlyGrowing && (
                        <div className={`absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-xs font-bold ${t.primary} text-white shadow-lg`}>
                          🌱
                        </div>
                      )}

                      {(() => {
                        const growthStage = getGrowthStage(acorn.currentMiles, acorn.milesNeeded);
                        return (
                          <div className="flex flex-col items-center gap-2 w-full">
                            {/* Show growth stage emoji based on progress */}
                            <div className="text-3xl">{growthStage.emoji}</div>
                            <div className="w-full">
                              <div className="text-sm font-semibold leading-tight break-words">
                                {isReady ? '✨ Ready!' : acorn.name}
                              </div>
                              <div className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} text-xs`}>
                                {isReady ? 'Tap!' : (
                                  <>
                                    <div>{growthStage.name}</div>
                                    <div>{acorn.currentMiles.toFixed(1)}/{acorn.milesNeeded}mi</div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Progress bar */}
                      {!isReady && (
                        <div className={darkMode ? 'bg-slate-900 rounded-full h-2' : 'bg-slate-300 rounded-full h-2'}>
                          <div
                            className={`${t.primary} h-2 rounded-full transition-all duration-500`}
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          />
                        </div>
                      )}
                    </CardWrapper>
                  );
                }

                if (slot.type === 'stash') {
                  const acorn = slot.data;
                  return (
                    <button
                      key={acorn.id}
                      onClick={() => {
                        setSelectedAcornToPlant(acorn);
                        setShowPlantModal(true);
                      }}
                      className={`p-4 rounded-xl border-2 ${darkMode ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-slate-50 border-slate-300 hover:border-slate-400'} flex flex-col items-center text-center gap-2 min-h-[130px] hover:scale-105 transition-transform`}
                    >
                      <div className="text-3xl">{acorn.emoji}</div>
                      <div className="w-full">
                        <div className="text-sm font-semibold leading-tight break-words">{acorn.name}</div>
                        <div className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} text-xs`}>
                            {acorn.milesNeeded}mi
                        </div>
                      </div>
                    </button>
                  );
                }

                return null;
              })}
            </div>
          )}
        </div>

        {/* Forest Expansion */}
        <div className={darkMode
          ? 'bg-slate-800 rounded-2xl p-6 mb-6 border border-slate-700'
          : 'bg-white rounded-2xl p-6 mb-6 shadow-md border border-slate-200'
        }>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className={'w-5 h-5 ' + t.text} />
            <h3 className="text-lg font-bold">Forest Expansions</h3>
          </div>

          <div className="space-y-3">
            {/* Expansion 1 */}
            <div className={`p-4 rounded-xl border-2 ${
              expansion1Unlocked
                ? darkMode ? 'bg-slate-700 border-emerald-600' : 'bg-emerald-50 border-emerald-400'
                : darkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-100 border-slate-300'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">{expansion1Unlocked ? '✅' : '🔒'}</div>
                  <div>
                    <div className="font-semibold">New Arboretum</div>
                    <div className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                      +3 slots
                    </div>
                  </div>
                </div>
                <div className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                  {expansion1Unlocked ? 'Unlocked!' : `${totalMiles.toFixed(0)}/50 miles`}
                </div>
              </div>
              {!expansion1Unlocked && (
                <div className="mt-2">
                  <div className={darkMode ? 'bg-slate-800 rounded-full h-2' : 'bg-slate-300 rounded-full h-2'}>
                    <div
                      className={`${t.primary} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${Math.min((totalMiles / 50) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Expansion 2 */}
            <div className={`p-4 rounded-xl border-2 ${
              expansion2Unlocked
                ? darkMode ? 'bg-slate-700 border-emerald-600' : 'bg-emerald-50 border-emerald-400'
                : darkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-100 border-slate-300'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">{expansion2Unlocked ? '✅' : '🔒'}</div>
                  <div>
                    <div className="font-semibold">The Sunlit Clearing</div>
                    <div className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                      +3 slots
                    </div>
                  </div>
                </div>
                <div className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                  {expansion2Unlocked
                    ? 'Unlocked!'
                    : !expansion1Unlocked
                      ? 'Unlock Expansion 1 first'
                      : `${totalMiles.toFixed(0)}/100 miles`}
                </div>
              </div>
              {!expansion2Unlocked && expansion1Unlocked && (
                <div className="mt-2">
                  <div className={darkMode ? 'bg-slate-800 rounded-full h-2' : 'bg-slate-300 rounded-full h-2'}>
                    <div
                      className={`${t.primary} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${Math.min((totalMiles / 100) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Forest Collection */}
        {forestCollection.length > 0 && (
          <div className={darkMode
            ? 'bg-slate-800 rounded-2xl p-6 border border-slate-700'
            : 'bg-white rounded-2xl p-6 shadow-md border border-slate-200'
          }>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className={'w-5 h-5 ' + t.text} />
                <h3 className="text-lg font-bold">Forest Collection</h3>
              </div>
              <div className={darkMode ? 'text-sm text-slate-400' : 'text-sm text-slate-600'}>
                {uniqueDiscoveries}/{totalPossibleTrees} discovered
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {Object.values(groupedCollection).map((tree) => (
                <div
                  key={tree.name}
                  className={darkMode
                    ? 'bg-slate-700 rounded-lg px-3 py-2 flex items-center gap-2 relative'
                    : 'bg-slate-100 rounded-lg px-3 py-2 flex items-center gap-2 relative'
                  }
                  title={`First grown on ${new Date(tree.firstGrown).toLocaleDateString()}`}
                >
                  <span className="text-xl">{tree.emoji}</span>
                  <span className="text-xs font-medium">{tree.name}</span>
                  {tree.count > 1 && (
                    <span className={`text-xs font-bold ml-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      ×{tree.count}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Plant Modal */}
      {showPlantModal && selectedAcornToPlant && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setShowPlantModal(false)}
        >
          <div
            className={darkMode
              ? 'bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-slate-700'
              : 'bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl'
            }
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">Plant Acorn?</h3>

            <div className={`p-4 rounded-xl mb-4 border-2 ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-300'}`}>
              <div className="flex items-center gap-3">
                <div className="text-4xl">{selectedAcornToPlant.emoji}</div>
                <div>
                  <div className="font-semibold">{selectedAcornToPlant.name}</div>
                  <div className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                    Needs {selectedAcornToPlant.milesNeeded} miles to grow
                  </div>
                  <div className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                    Will become: ❔ Mystery!
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => plantAcorn(selectedAcornToPlant)}
                className={`w-full px-4 py-3 rounded-xl ${t.primary} ${t.hover} text-white transition-colors font-medium`}
              >
                Plant 🌱
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPlantModal(false)}
                  className={darkMode
                    ? 'flex-1 px-4 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors font-medium'
                    : 'flex-1 px-4 py-3 rounded-xl bg-slate-200 hover:bg-slate-300 transition-colors font-medium'
                  }
                >
                  Cancel
                </button>
                <button
                  onClick={showDiscardConfirmation}
                  className={darkMode
                    ? 'flex-1 px-4 py-3 rounded-xl bg-red-900 hover:bg-red-800 text-red-200 transition-colors font-medium'
                    : 'flex-1 px-4 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors font-medium'
                  }
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Celebration Modal */}
      {showCelebration && harvestingSeed && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
          onClick={completeCelebration}
        >
          <div
            className={darkMode
              ? 'bg-slate-800 rounded-2xl p-8 max-w-md w-full border-2 border-emerald-500 shadow-2xl'
              : 'bg-white rounded-2xl p-8 max-w-md w-full border-2 border-emerald-400 shadow-2xl'
            }
            onClick={(e) => e.stopPropagation()}
          >
            {/* Celebration content */}
            <div className="text-center">
              <div className="mb-4">
                <Sparkles className={'w-12 h-12 mx-auto mb-2 ' + t.text} />
                <h3 className="text-2xl font-bold mb-2">Congratulations!</h3>
                <p className={darkMode ? 'text-slate-400 font-quicksand' : 'text-slate-600 font-quicksand'}>
                  Your seed has sprouted!
                </p>
              </div>

              {/* Revealed plant */}
              <div className={`p-6 rounded-2xl mb-6 ${darkMode ? 'bg-gradient-to-br from-emerald-900 to-emerald-800' : 'bg-gradient-to-br from-emerald-100 to-emerald-200'}`}>
                <div className="text-6xl mb-3 animate-bounce">{harvestingSeed.result.emoji}</div>
                <div className="text-xl font-bold mb-1">{harvestingSeed.result.name}</div>
                <div className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                  Grown from {harvestingSeed.milesNeeded} miles of running!
                </div>
              </div>

              <button
                onClick={completeCelebration}
                className={`w-full px-6 py-3 rounded-xl ${t.primary} ${t.hover} text-white transition-colors font-medium`}
              >
                Add to Collection ✨
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {showInfoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
          onClick={() => setShowInfoModal(false)}
        >
          <div
            className={darkMode
              ? 'bg-slate-800 rounded-2xl p-5 max-w-lg w-full border border-slate-700 shadow-2xl'
              : 'bg-white rounded-2xl p-5 max-w-lg w-full shadow-2xl'
            }
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold mb-1">Help Scout the Squirrel grow his garden!</h3>
              <p className={darkMode ? 'text-slate-400 text-xs' : 'text-slate-600 text-xs'}>
                Collect mystery seeds and grow a forest with your runs
              </p>
            </div>

            <div className="grid gap-3 mb-4">
              {/* Step 1 */}
              <div className={darkMode ? 'bg-slate-700 rounded-lg p-3 border-2 border-slate-600' : 'bg-slate-50 rounded-lg p-3 border-2 border-slate-200'}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-6 h-6 rounded-full ${t.primary} text-white flex items-center justify-center font-bold text-xs`}>1</div>
                  <h4 className="font-bold text-sm">Collect & Plant</h4>
                </div>
                <p className={darkMode ? 'text-slate-300 text-xs leading-snug' : 'text-slate-700 text-xs leading-snug'}>
                  Complete runs to collect mystery seeds (~1 per 3 miles). Tap seeds to plant them.
                </p>
              </div>

              {/* Step 2 */}
              <div className={darkMode ? 'bg-slate-700 rounded-lg p-3 border-2 border-slate-600' : 'bg-slate-50 rounded-lg p-3 border-2 border-slate-200'}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-6 h-6 rounded-full ${t.primary} text-white flex items-center justify-center font-bold text-xs`}>2</div>
                  <h4 className="font-bold text-sm">Water with Miles</h4>
                </div>
                <p className={darkMode ? 'text-slate-300 text-xs leading-snug' : 'text-slate-700 text-xs leading-snug'}>
                  Miles water the first seed until ready, then flow to the next seed automatically.
                </p>
              </div>

              {/* Step 3 */}
              <div className={darkMode ? 'bg-slate-700 rounded-lg p-3 border-2 border-slate-600' : 'bg-slate-50 rounded-lg p-3 border-2 border-slate-200'}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className={`w-6 h-6 rounded-full ${t.primary} text-white flex items-center justify-center font-bold text-xs`}>3</div>
                  <h4 className="font-bold text-sm">Harvest & Collect</h4>
                </div>
                <p className={darkMode ? 'text-slate-300 text-xs leading-snug' : 'text-slate-700 text-xs leading-snug'}>
                  When ready, tap "Sprout!" to reveal your plant and add it to your collection.
                </p>
              </div>
            </div>

            {/* Rarity Tip */}
            <div className={darkMode ? 'bg-slate-900 rounded-lg p-3 border border-slate-700 mb-4' : 'bg-amber-50 rounded-lg p-3 border border-amber-200 mb-4'}>
              <p className={darkMode ? 'text-slate-300 text-xs mb-1' : 'text-slate-700 text-xs mb-1'}>
                <strong>💡 Tip: Rarity System</strong>
              </p>
              <p className={darkMode ? 'text-slate-400 text-xs leading-snug' : 'text-slate-600 text-xs leading-snug'}>
                Seeds have different rarities: <strong>Common</strong> (70%, 3-5 mi), <strong>Uncommon</strong> (20%, 6-8 mi), <strong>Rare</strong> (8%, 10-15 mi), <strong>Legendary</strong> (2%, 20-30 mi).
              </p>
            </div>

            <button
              onClick={() => setShowInfoModal(false)}
              className={`w-full px-4 py-2.5 rounded-xl ${t.primary} ${t.hover} text-white transition-colors font-medium text-sm`}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Discard Confirmation Modal */}
      {showDiscardConfirm && selectedAcornToPlant && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => {
            setShowDiscardConfirm(false);
            setSelectedAcornToPlant(null);
          }}
        >
          <div
            className={darkMode
              ? 'bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-slate-700'
              : 'bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl'
            }
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">Discard Seed?</h3>

            <p className={darkMode ? 'text-slate-300 mb-4' : 'text-slate-700 mb-4'}>
              Are you sure you want to discard this seed? This action cannot be undone.
            </p>

            <div className={`p-4 rounded-xl mb-4 border-2 ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-300'}`}>
              <div className="flex items-center gap-3">
                <div className="text-3xl">{selectedAcornToPlant.emoji}</div>
                <div>
                  <div className="font-semibold">{selectedAcornToPlant.name}</div>
                  <div className={darkMode ? 'text-slate-400 text-sm' : 'text-slate-600 text-sm'}>
                    {selectedAcornToPlant.milesNeeded} miles to grow
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDiscardConfirm(false);
                  setShowPlantModal(true);
                }}
                className={darkMode
                  ? 'flex-1 px-4 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors font-medium'
                  : 'flex-1 px-4 py-3 rounded-xl bg-slate-200 hover:bg-slate-300 transition-colors font-medium'
                }
              >
                Cancel
              </button>
              <button
                onClick={discardAcorn}
                className={darkMode
                  ? 'flex-1 px-4 py-3 rounded-xl bg-red-900 hover:bg-red-800 text-red-200 transition-colors font-medium'
                  : 'flex-1 px-4 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors font-medium'
                }
              >
                Yes, Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection Guide Modal */}
      {showCollectionGuide && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
          onClick={() => setShowCollectionGuide(false)}
        >
          <div
            className={darkMode
              ? 'bg-slate-800 rounded-2xl p-6 max-w-2xl w-full border border-slate-700 shadow-2xl max-h-[80vh] overflow-y-auto'
              : 'bg-white rounded-2xl p-6 max-w-2xl w-full shadow-2xl max-h-[80vh] overflow-y-auto'
            }
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <Book className={'w-8 h-8 ' + t.text} />
              <h3 className="text-2xl font-bold">Collection Guide</h3>
            </div>

            <p className={darkMode ? 'text-slate-400 mb-4' : 'text-slate-600 mb-4'}>
              Discover all {acornTypes.length} unique trees! Collected trees are shown in color, undiscovered trees are grayed out.
            </p>

            {/* Group trees by rarity */}
            {['common', 'uncommon', 'rare', 'legendary'].map(rarity => {
              const trees = acornTypes.filter(t => t.rarity === rarity);
              const rarityLabel = rarity.charAt(0).toUpperCase() + rarity.slice(1);
              const rarityEmoji = rarity === 'common' ? '🌰' : rarity === 'uncommon' ? '🌰' : rarity === 'rare' ? '🌰' : '💎';

              return (
                <div key={rarity} className="mb-6">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <span className="text-xl">{rarityEmoji}</span>
                    <span>{rarityLabel}</span>
                    <span className={darkMode ? 'text-slate-400 text-sm font-normal' : 'text-slate-600 text-sm font-normal'}>
                      ({trees.length} trees)
                    </span>
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {trees.map(tree => {
                      const isDiscovered = forestCollection.some(c => c.name === tree.result.name);
                      return (
                        <div
                          key={tree.id}
                          className={`p-3 rounded-lg border-2 ${
                            isDiscovered
                              ? darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-300'
                              : darkMode ? 'bg-slate-900 border-slate-800 opacity-40' : 'bg-slate-100 border-slate-200 opacity-40'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{isDiscovered ? tree.result.emoji : '❔'}</span>
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-semibold truncate ${isDiscovered ? '' : 'text-slate-500'}`}>
                                {isDiscovered ? tree.result.name : '???'}
                              </div>
                              <div className={darkMode ? 'text-slate-500 text-xs' : 'text-slate-400 text-xs'}>
                                {tree.milesNeeded}mi
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <button
              onClick={() => setShowCollectionGuide(false)}
              className={`w-full mt-4 px-6 py-3 rounded-xl ${t.primary} ${t.hover} text-white transition-colors font-medium`}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoutView;
